import { useState } from 'react';

import DeleteModal from '@atom/components/common/DeleteModal';
import WorkUsers from '@atom/components/common/workUsers/WorkUsers';
import { Icon, IconButton, TextField } from '@atom/mui';
import { DeleteIcon } from '@atom/mui/icons';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import { DraftTask } from '../../CreateWorkModalContext';
import {
  canDeleteTask,
  canRenameTask,
  DEFAULT_TASK_NAME,
} from '../../createWorkModalUtilities';

import classes from './tasks.module.css';

type TaskCardProps = {
  task: DraftTask;
  onRename: (name: string) => void;
  onDelete: () => void;
  onNameFocus: () => void;
  onNameBlur: () => void;
};

const normalizeUserGroups = (task: DraftTask): any[] =>
  (task.userGroups || []).map(group => ({
    ...group,
    id: (group as any).id ?? (group as any).userGroupId,
  }));

const getDueDateLabel = (task: DraftTask): string =>
  task.dueDate ? setDisplayDate(task.dueDate) : 'No Due Date';

const TaskCard = ({
  task,
  onRename,
  onDelete,
  onNameFocus,
  onNameBlur,
}: TaskCardProps) => {
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const canRename = canRenameTask(task);
  const canDelete = canDeleteTask(task);

  const handleBlur = () => {
    if (!task.name?.trim()) {
      onRename(DEFAULT_TASK_NAME);
    }

    onNameBlur();
  };

  return (
    <div className={classes.taskRow} data-cy="createWorkModalTaskCard">
      <div className={classes.taskRowInfo}>
        {canRename ? (
          <TextField
            fullWidth
            required
            value={task.name}
            onFocus={onNameFocus}
            onChange={event => onRename(event.target.value)}
            onBlur={handleBlur}
            data-cy="createWorkModalTaskNameInput"
          />
        ) : (
          <div className={classes.taskRowName}>{task.name}</div>
        )}
        <div className={classes.taskRowMeta}>
          <WorkUsers
            users={task.users as any}
            userGroups={normalizeUserGroups(task)}
            avatarSize={28}
          />
        </div>
        <div className={classes.taskRowMeta}>{getDueDateLabel(task)}</div>
      </div>
      {canDelete && (
        <IconButton
          onClick={() => setDeleteOpen(true)}
          data-cy="createWorkModalTaskDeleteButton"
        >
          <Icon icon={DeleteIcon} />
        </IconButton>
      )}
      <DeleteModal
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          onDelete();
          setDeleteOpen(false);
        }}
        title="Delete Task?"
        content={
          <span>
            Are you sure you want to delete task{' '}
            <strong>"{task.name?.trim() || DEFAULT_TASK_NAME}"</strong>?
          </span>
        }
      />
    </div>
  );
};

export default TaskCard;
